/**
 * EAV.RDP.ANALYTICS, Type FinancialContracts: IPA's answer laid out as LSEG's Excel add-in
 * (Refinitiv for Office 26.30.2) lays it out, and IPA's refusals as LSEG shows them.
 *
 *   BaseFinancialContractsResponseProcessor (24686)   ErrorMessage rows, the in/fd display
 *     (every FC subtype's res processor is it,        options, Field[n] / Field.key accessors,
 *     OptionResProcessor (37565) aside: its row        list fields over several rows, objects
 *     header is InstrumentCode, else FxCrossCode)      over several columns
 *   BaseResponseProcessor (63223)                     rh/ch layouts, transpose, dates, empty rows
 *   BaseHelpResponseProcessor (64650),                Type Help: LSEG's own About table, laid
 *     AboutResponseProcessor (81713)                  out with no request
 *   ErrorHandler (13859).getErrorResponse             the text of a refusal or of a failed layout
 *
 * Built on BaseResponseProcessor (src/adfin/responseBase.ts) and the AdFin grid helpers
 * (src/adfin/responses.ts): dates become date serials, objects (an ObjectArray's items) #N/A,
 * and an empty formula-cell value the empty corner where the static writer puts "Updated at".
 */

import {
  BaseResponseProcessor,
  ClientResponse,
  FieldValues,
  ResponseHeader,
  capitalize,
  errorMessage,
  requestorError,
} from "../adfin/responseBase";
import { isEmptyCallerValue, markCell, toGrid, trimTrailingBlanks } from "../adfin/responses";
import { toTable } from "../lib/grids";
import { Table } from "../lib/layout";
import { errorToCellText, searchRefusal } from "../lib/lsegText";
import { NaError } from "../lib/refinitiv";
import { CurvesResProcessor } from "./curvesResponse";
import { SurfacesResProcessor } from "./surfacesResponse";
import { buildAnalyticsRequest, CurvesRequest, DatesRequest, HelpRequest, isHolidayOutput, SurfacesRequest } from "./request";

/** IPA's financial-contracts answer: `{ headers, data }` (a getFields answer has headers only), or an error. */
export type AnalyticsAnswer = { headers?: ResponseHeader[]; data?: unknown[][]; error?: unknown };

const SEP = ";"; // LSEG: ProcessorsConfig.defaultSeparator (27821)

// LSEG: BaseFCResProcessorConfig_7376.regexPatterns.outputField: name, then [n] (3), .key (4) or a bad [..] (5)
const OUTPUT_FIELD = /(\w+)(\[(\d+)\]|\.(\w+)|(\[[\d-.]+\]))/;

// LSEG: Translator (40879) texts, mod_47243
const ACCESSOR_TEXT = {
  nonList: "For field {{fieldName}}:Accessing non-list with index accessor",
  bigIndex: "{{fieldName}}: Index must be between 0 and {{maxIndex}}",
  negativeOrFloating: "{{fieldName}}: List index acessor must be 0 or positive whole number",
  nonObject: "For field {{fieldName}}:Accessing non-object with dot accessor",
  unexistentKey: "For field {{fieldName}}:Accessing object with unexistent key.",
};
const accessorError = (text: string, fieldName: string, maxIndex?: number) =>
  new Error(text.replace("{{fieldName}}", fieldName).replace("{{maxIndex}}", `${maxIndex}`));

// LSEG: flattenArray1Level_75285.flattenArray1Level
const flattenArray1Level = <T>(list: (T | T[])[]): T[] => list.reduce<T[]>((out, v) => [...out, ...(Array.isArray(v) ? v : [v])], []);

/** An object's leaves by path: `{a: 1, b: {c: 2}}` → `{".a": 1, ".b.c": 2}`. (LSEG's walk fails on a null leaf.) */
// LSEG: flattenArray1Level_75285.getStringAccKeyObj
function getStringAccKeyObj(obj: object): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const walk = (o: object, prefix = "") => {
    for (const [key, v] of Object.entries(o)) {
      const path = `${prefix}.${key}`;
      if (typeof v === "object") walk(v as object, path);
      else out[path] = v;
    }
  };
  walk(obj);
  return out;
}

const isPlainObject = (v: unknown): v is Record<string, unknown> => !!v && (v as object).constructor === Object;

// LSEG: BaseFinancialContractsResponseProcessor_24686 (with OptionResProcessor_37565's row header)
class FinancialContractsResProcessor extends BaseResponseProcessor {
  /** The answer was an ErrorMessage shown alone: LSEG puts it in the formula cell, whatever the Dest. */
  errorAlone = false;

  constructor(private readonly optionIds: boolean) {
    super();
  }

  /**
   * The answer as Excel gets it. A row with an ErrorMessage repeats it in every column but the
   * first when there are several rows; alone, the message is the whole result.
   * `options` and `output` are the request's (Options cleaned, Output else Universe).
   */
  // LSEG: BaseFinancialContractsResponseProcessor_24686.response
  response(e: AnalyticsAnswer, options: string, output: string): ClientResponse {
    const i = e.headers?.findIndex((h) => h.name.toLowerCase() === "errormessage") ?? -1;
    const data = e.data;
    const alone = data?.find((row, r) => {
      const message = row[i];
      if (message && data.length > 1) {
        const first = row[0];
        data[r] = row.map(() => message);
        data[r][0] = first;
        return false;
      }
      return !!message;
    });
    if (alone) {
      this.errorAlone = true;
      return { callerValue: alone[i], positionedData: [] };
    }
    const [option, dt] = this.getDisplayOptions(options, "in", "fd");
    const showInvalid = this.showInvalidField(options);
    const values = this.getDisplayData(e, output, dt, showInvalid);
    const laid = this.transposeData(this.rhChFormat(values, option), options);
    return this.removeEmptyRows(this.clientFormat(laid));
  }

  /**
   * LSEG's third display option. Its pattern has one group but it reads a second one, so a
   * `showInvalidField:y…` or `:n…` throws: `Invalid Field` never shows (golden an2_invalid_show_yes).
   */
  // LSEG: BaseResponseProcessor_63223.getDisplayOptions (its third value)
  showInvalidField(options: string): boolean {
    if (!/(showInvalidField):[yn]/i.test(options)) return false;
    throw new TypeError("Cannot read properties of undefined (reading 'toLowerCase')");
  }

  // LSEG: BaseFinancialContractsResponseProcessor_24686.manageInvalidField
  manageInvalidField(row: unknown[], e: AnalyticsAnswer): void {
    e.headers!.forEach((h, r) => {
      if (h.type.toLowerCase() === "invalidfield") row.splice(r, 1, "Invalid Field");
    });
  }

  // LSEG: BaseFinancialContractsResponseProcessor_24686.getDisplayData
  getDisplayData(e: AnalyticsAnswer, output: string, dt: boolean, showInvalid: boolean): FieldValues {
    if (output.toLowerCase() === "getfields") return this.getFieldsDisplayData(e);
    const lengths = this.getMaxLengthArrInCollectionLengths(e, output);
    return {
      requestIDs: this.getRequestIds(e, lengths),
      fields: this.getFields(e, output, dt),
      valueSets: this.getValueSets(e, output, lengths, showInvalid),
    };
  }

  /** The column names: the Output's fields as typed (capitalized), an object field one per leaf, or (dt) the header types. */
  // LSEG: BaseFinancialContractsResponseProcessor_24686.getFields
  getFields(e: AnalyticsAnswer, output: string, dt: boolean): string[] {
    const names = output.split(SEP).map((raw): string | string[] => {
      const field = capitalize(raw);
      const m = field.match(OUTPUT_FIELD);
      const name = m ? m[1] : field;
      const index = e.headers?.findIndex((h) => h.name.toLowerCase() === name.toLowerCase()) ?? -1;
      const header = e.headers![index];
      const first = e.data && e.data[0] && e.data[0][index];
      const dot = !!m && !!m[4];
      if (isPlainObject(first) && !dot) return Object.keys(getStringAccKeyObj(first)).map((key) => name + key);
      return dt ? header.type : field;
    });
    return flattenArray1Level(names);
  }

  /** getFields: one row per header, its type then its name. */
  // LSEG: BaseFinancialContractsResponseProcessor_24686.getFieldsDisplayData
  getFieldsDisplayData(e: AnalyticsAnswer): FieldValues {
    const headers = e.headers!;
    return { requestIDs: headers.map((h) => h.name || ""), fields: ["type", "name"], valueSets: headers.map((h) => [h.type, h.name]) };
  }

  /** Rows per answer row: the longest list among the Output's list fields (header type …List / …Array), at least 1. */
  // LSEG: BaseFinancialContractsResponseProcessor_24686.getMaxLengthArrInCollectionLengths
  getMaxLengthArrInCollectionLengths(e: AnalyticsAnswer, output: string): number[] {
    const lengths: number[] = [];
    if (!e.data) return lengths;
    e.data.forEach((row) => {
      let max = 1;
      e.headers?.forEach((h, r) => {
        if (!/list|array/i.test(h.type) || !`${output}${SEP}`.match(new RegExp(`${h.name}${SEP}`, "i"))) return;
        const length = ((row[r] || []) as { length: number }).length;
        if (length > max) max = length;
      });
      lengths.push(max);
    });
    return lengths;
  }

  /** The row header of each row: the answer row's first value (an option's: InstrumentCode, else FxCrossCode). */
  // LSEG: BaseFinancialContractsResponseProcessor_24686.getRequestIds, OptionResProcessor_37565.getDisplayData
  getRequestIds(e: AnalyticsAnswer, lengths: number[]): unknown[] {
    let ids: unknown[] = [];
    if (!e.data) return ids;
    e.data.forEach((row, n) => {
      const id = this.optionIds ? row[0] || row[1] : row[0];
      ids = [...ids, ...Array(lengths[n]).fill(id).map((v) => this.getValueIfEmpty(v))];
    });
    return ids;
  }

  // LSEG: BaseFinancialContractsResponseProcessor_24686.getValueSets
  getValueSets(e: AnalyticsAnswer, output: string, lengths: number[], showInvalid: boolean): unknown[][] {
    const sets = e.data?.map((row, i) => {
      if (showInvalid) this.manageInvalidField(row, e);
      return this.getInnerValueSets(output, lengths[i], row, e);
    });
    return sets ? flattenArray1Level(sets) : [];
  }

  /** The `count` rows of one answer row: each Output field's value (a list's k-th item on row k). */
  // LSEG: BaseFinancialContractsResponseProcessor_24686.getInnerValueSets
  getInnerValueSets(output: string, count: number, row: unknown[], e: AnalyticsAnswer): unknown[][] {
    const fields = output.split(SEP);
    const rows: unknown[][] = [];
    for (let k = 0; k < count; k++) {
      const values = fields.map((f) => this.getValueSet(k, f, row, e));
      rows.push(flattenArray1Level(values).map((v) => this.getValueIfEmpty(v)));
    }
    return rows;
  }

  // LSEG: BaseFinancialContractsResponseProcessor_24686.getValueSet, getFieldDataType
  getValueSet(k: number, raw: string, row: unknown[], e: AnalyticsAnswer): unknown {
    const field = capitalize(raw);
    const m = field.match(OUTPUT_FIELD);
    const value = this.getFieldDataValue(field, row, e, m);
    const indexAccessor = !!m && !!m[3];
    const badIndex = !!m && !!m[5];
    const dotAccessor = !!m && !!m[4];
    const isList = Array.isArray(value);
    const isObject = isPlainObject(value);
    if (badIndex) throw accessorError(ACCESSOR_TEXT.negativeOrFloating, field);
    if (indexAccessor && !isList) throw accessorError(ACCESSOR_TEXT.nonList, field);
    if (this.isFieldDataValueEmpty(value)) return value;
    if (dotAccessor && !isObject) throw accessorError(ACCESSOR_TEXT.nonObject, field);
    if (isList) return this.getArrayFieldValue(k, field, indexAccessor, value as unknown[], m);
    return isObject ? this.getObjectFieldValue(field, value as Record<string, unknown>, dotAccessor, m) : value;
  }

  // LSEG: BaseFinancialContractsResponseProcessor_24686.getArrayFieldValue
  getArrayFieldValue(k: number, field: string, indexAccessor: boolean, list: unknown[], m: RegExpMatchArray | null): unknown {
    if (!indexAccessor) return list[k];
    const index = Number(m![3]);
    const maxIndex = list.length - 1;
    if (index > maxIndex) throw accessorError(ACCESSOR_TEXT.bigIndex, field, maxIndex);
    return list[index];
  }

  /** `Field.key` is that key's value; a plain object field gives its leaves' values. */
  // LSEG: BaseFinancialContractsResponseProcessor_24686.getObjectFieldValue
  getObjectFieldValue(field: string, obj: Record<string, unknown>, dotAccessor: boolean, m: RegExpMatchArray | null): unknown {
    if (!dotAccessor) return Object.values(getStringAccKeyObj(obj));
    const key = m![4];
    if (!Object.prototype.hasOwnProperty.call(obj, key)) throw accessorError(ACCESSOR_TEXT.unexistentKey, field);
    return obj[key];
  }

  // LSEG: BaseFinancialContractsResponseProcessor_24686.isFieldDataValueEmpty
  isFieldDataValueEmpty(v: unknown): boolean {
    return !(String(v) !== "" && v != null && !/null/i.test(String(v)));
  }

  // LSEG: BaseFinancialContractsResponseProcessor_24686.getFieldDataValue
  getFieldDataValue(field: string, row: unknown[], e: AnalyticsAnswer, m: RegExpMatchArray | null): unknown {
    const name = m ? m[1] : field;
    return row[e.headers!.findIndex((h) => h.name.toLowerCase() === name.toLowerCase())];
  }
}

/** LSEG's own answer to Help/About: its analytics library's version, which EAV repeats as is. */
// LSEG: AboutResponseProcessor_81713.prepareAboutInfo, with the version of module 49336
const ABOUT_INFO = {
  headers: [
    { type: "String", name: "Component" },
    { type: "String", name: "Version" },
  ],
  data: [["OfficeAnalyticsLibrary", "26.0.11"]],
};

// LSEG: BaseHelpResponseProcessor_64650 with AboutResponseProcessor_81713
class AboutResProcessor extends BaseResponseProcessor {
  /** No Options: the help processor's own "RH:IN CH:Fd", with `in` read as `id`. */
  // LSEG: BaseHelpResponseProcessor_64650.response
  response(options: string, output: string): ClientResponse {
    const opts = options || "RH:IN CH:Fd";
    const [option, dt] = this.getDisplayOptions(opts, "in", "fd");
    const laid = this.transposeData(this.rhChFormat(this.getDisplayData(output, dt), option), opts);
    return this.removeEmptyRows(this.clientFormat(laid));
  }

  /** Each row's first value is its row header; getFields lists the headers, type then name. */
  // LSEG: AboutResponseProcessor_81713.getDisplayData
  getDisplayData(output: string, dt: boolean): FieldValues {
    const { headers, data } = ABOUT_INFO;
    if (output.toLowerCase() === "getfields") {
      return { requestIDs: headers.map((h) => h.name || ""), fields: ["type", "name"], valueSets: headers.map((h) => [h.type, h.name]) };
    }
    return {
      requestIDs: data.map((row) => row[0] || ""),
      fields: headers.slice(1).map((h) => (dt ? h.type : h.name)),
      valueSets: data.map((row) => row.slice(1).map((v) => this.getValueIfEmpty(v))),
    };
  }
}

// ---------------------------------------------------------------------------------------------
// Type DatesAndCalendars

type DatesItem = Record<string, unknown> & { holidays?: Holiday[] };
type Holiday = { date?: string; names?: HolidayName[] };
type HolidayName = { name?: string; calendars?: string[]; countries?: string[] };

/**
 * One row per request (Output's fields, their values looked up in any case), or one per holiday
 * name and calendar (or country) when the answer lists holidays. With no ch/rh option the
 * header row is dropped; rh:fd transposes.
 */
// LSEG: BaseDateAndCalendarResponseProcessor (81117), GetHolidaysResponseProcessor (44978),
// DateScheduleResponseProcessor (35450)
class DatesResProcessor extends BaseResponseProcessor {
  constructor(private readonly req: DatesRequest) {
    super();
  }

  // LSEG: BaseDateAndCalendarResponseProcessor_81117.response
  response(e: unknown): ClientResponse {
    const [option] = this.getDisplayOptions(this.req.options);
    const fields = this.getFields();
    const rows = this.req.dates.startsWith("dateschedule") ? this.scheduleData(e as { dates: string[] }) : this.getDisplayData(e as DatesItem[], fields);
    const laid = this.transposeData(this.dacRhChFormat(rows, option), this.req.options);
    return this.removeEmptyRows(this.clientFormat(laid));
  }

  // LSEG: DateScheduleResponseProcessor_35450.getDisplayData (its fields are ignored)
  scheduleData(e: { dates: string[] }): unknown[][] {
    return [["dates"], ...e.dates.map((d) => [d.replace("T00:00:00Z", "")])];
  }

  // LSEG: BaseDateAndCalendarResponseProcessor_81117.dacRhChFormat
  dacRhChFormat(rows: unknown[][], option: string): unknown[][] {
    if (/rh:fd/i.test(option)) return this.transposeData(rows);
    if (!/rh:|ch:/i.test(option)) return rows.slice(1);
    return rows;
  }

  /** Output's names as typed (holidays: `tag` alone, if asked), then the holiday columns. */
  // LSEG: BaseDateAndCalendarResponseProcessor_81117.getFields
  getFields(): string[] {
    if (this.req.outputMissing) throw new TypeError(`Cannot read properties of ${this.req.outputMissing} (reading 'split')`);
    let names = this.req.output.split(SEP);
    if (this.req.dates === "holidays") names = names.find((f) => /tag/i.test(f)) ? ["tag"] : [];
    return [...names, ...this.getHolidayFields()];
  }

  /**
   * holidays: Output's holiday names (`holidays.date`…); the others: those of
   * `holidayOutputs:[…]` in UniverseParams (else Options), Names read as `holidays.name`.
   */
  // LSEG: BaseDateAndCalendarResponseProcessor_81117.getHolidayFields, GetHolidaysResponseProcessor_44978.getHolidayFields
  getHolidayFields(): string[] {
    if (this.req.dates === "holidays") {
      const output = this.req.output;
      if (output.trim() === "") return [];
      const names = output.split(SEP);
      const tag = names.indexOf("tag");
      if (tag !== -1) names.splice(tag, 1);
      return names.map((s) => s.toLowerCase()).filter(isHolidayOutput).map((s) => `holidays.${s}`);
    }
    const params = this.req.universeParams;
    const source = params.indexOf("holidayOutputs") !== -1 ? params : this.req.options;
    let re = /holidayOutputs[:=](.*)/i;
    const token = source.split(" ").find((t) => re.test(t));
    if (!token) return [];
    if (token.includes("[") && token.includes("]")) re = /holidayOutputs[:=]\[(.*?)\]/i;
    return token
      .match(re)![1]
      .split(SEP)
      .map((s) => s.toLowerCase())
      .filter(isHolidayOutput)
      .map((s) => (s === "names" ? "holidays.name" : `holidays.${s}`));
  }

  // LSEG: BaseDateAndCalendarResponseProcessor_81117.getDisplayData, addEmptyStringsToShorterRows
  getDisplayData(e: DatesItem[], fields: string[]): unknown[][] {
    const rows: unknown[][] = [fields, ...e.reduce<unknown[][]>((out, r) => [...out, ...this.getRequestHolidays(r, fields)], [])];
    const width = Math.max(...rows.map((r) => r.length));
    return rows.map((r) => [...r, ...Array(width - r.length).fill("")]);
  }

  // LSEG: BaseDateAndCalendarResponseProcessor_81117.getRequestHolidays
  getRequestHolidays(r: DatesItem, fields: string[]): unknown[][] {
    if (!(r.holidays && r.holidays.length > 0)) return [this.getRequestValues(r, fields)];
    return r.holidays.reduce<unknown[][]>((out, h) => [...out, ...this.getHolidayNames(h, r, fields)], []);
  }

  /** A holiday's rows: one per name and per calendar (or country, the longer list); none without names. */
  // LSEG: BaseDateAndCalendarResponseProcessor_81117.getHolidayNames, calendarsOrCountries
  getHolidayNames(h: Holiday, r: DatesItem, fields: string[]): unknown[][] {
    if (!h.names) return [[""]];
    return h.names.reduce<unknown[][]>((out, n) => {
      const list = ((n.calendars ? n.calendars.length : 0) > (n.countries ? n.countries.length : 0) ? n.calendars : n.countries) as string[];
      return [...out, ...list.map((_, i) => [...this.getRequestValues(r, fields), ...this.getSpecificHolidayValues(h, n, i, fields)])];
    }, []);
  }

  // LSEG: BaseDateAndCalendarResponseProcessor_81117.getRequestValues
  getRequestValues(r: DatesItem, fields: string[]): unknown[] {
    const lower: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(r)) lower[k.toLowerCase()] = v;
    return fields.filter((f) => !/holidays/i.test(f)).map((f) => this.getValueIfEmpty(lower[f.toLowerCase()]));
  }

  // LSEG: BaseDateAndCalendarResponseProcessor_81117.getSpecificHolidayValues
  getSpecificHolidayValues(h: Holiday, n: HolidayName, i: number, fields: string[]): unknown[] {
    const out: unknown[] = [];
    for (const f of fields) {
      if (f === "holidays.date") out.push(h.date || "");
      else if (f === "holidays.name" || f === "holidays.names") out.push(n.name || "");
      else if (f === "holidays.calendars") out.push(n.calendars && n.calendars[i] ? n.calendars[i] : "");
      else if (f === "holidays.countries") out.push(n.countries && n.countries[i] ? n.countries[i] : "");
    }
    return out.map((v) => this.getValueIfEmpty(v));
  }
}

/**
 * The service's answer as LSEG's processor reads it: the requests' results as an array. The
 * hub relays the service's JSON as is, `{value: [...]}` (dates-add-periods, 2026-10-29); LSEG's
 * code reads a bare array [C], so `value` is unwrapped. date-schedule answers `{dates: [...]}`.
 */
function datesAnswer(answer: unknown, dates: string): unknown {
  if (dates.startsWith("dateschedule") || Array.isArray(answer)) return answer;
  const value = (answer as { value?: unknown } | null)?.value;
  return Array.isArray(value) ? value : answer;
}

/**
 * The first request's error, as LSEG's requestor refuses the whole answer for it: a 400 whose
 * statusText and text toResponseError joins with a space (at_dacu_add_bad_cal: "Bad request
 * format Business Calendar: calendar not found.").
 */
// LSEG: BaseQPSRequestor.validateRequestStatus, validateRequestData (77442), toResponseError (48474)
function datesAnswerError(answer: unknown): unknown {
  type ApiError = { status?: unknown; code?: unknown; message?: unknown; serviceName?: unknown; diagnosticMessage?: unknown };
  const a = answer as (ApiError & { error?: unknown }) | unknown[] | null;
  if (Array.isArray(a)) {
    const e = (a[0] as { error?: ApiError } | undefined)?.error;
    return e ? { message: `${e.serviceName ?? "Bad request format"} ${e.diagnosticMessage || e.message}` } : null;
  }
  if (a && (a.status === "Error" || a.status === "error")) return { message: `${a.code ?? "Bad request format"} ${a.message ?? "Server error"}` };
  return a?.error ?? null;
}

// ---------------------------------------------------------------------------------------------
// The layout

/**
 * IPA's answers to the batches, as one: the first answer's headers, every answer's rows in
 * order (LSEG sends one request; EAV's batches of 10 are cut in Universe order). The rows are
 * copies: LSEG's processor writes into them. An answer that is an error stands for the request.
 */
function mergeAnswers(answers: unknown[]): AnalyticsAnswer {
  const list = answers as (AnalyticsAnswer | null | undefined)[];
  const failed = list.find((a) => a?.error); // LSEG: `if (e.error)`, as the AdFin path (executorResult)
  if (failed) return failed;
  if (!list.some((a) => Array.isArray(a?.data))) return list[0] ?? {};
  const rows = list.reduce<unknown[][]>((out, a) => (Array.isArray(a?.data) ? [...out, ...a!.data] : out), []);
  return { ...list[0], data: rows.map((row) => (Array.isArray(row) ? [...row] : row)) };
}

/** A cell as the table holds it: dates as date serials, and an object (an ObjectArray's item) as #N/A. */
// [A] LSEG hands Excel the object; the goldens show #N/A (an2_bondfuture, an3_bondfuture_basket).
const markAnalyticsCell = (v: unknown): unknown => (v !== null && typeof v === "object" ? new NaError() : markCell(v));

/** A text LSEG shows in the formula cell, whatever the Dest. */
const textTable = (text: string): Table => ({ ...toTable([[text]]), inFormulaCell: true });

/**
 * LSEG's grid for `RDP.Analytics(...args)` given IPA's answers to its batches (in order), as
 * the static writer takes it: the formula cell's value at the top-left, or the empty corner
 * (`cornerEmpty`) where it puts "Updated at"; a Dest places the whole grid (planOutput).
 * An ErrorMessage alone, an error answer and a failed layout are one text in the formula cell.
 * `args` are as for `buildAnalyticsRequest`, which gives the request's Output and Options.
 */
// LSEG: QPSService.process (36844): the response processor's response(answer, payload),
// and processSingleResponse's catch: ErrorHandler.getErrorResponse, then BaseResponseProcessor.response
export function layAnalyticsResponse(args: unknown[], answers: unknown[]): Table {
  const built = buildAnalyticsRequest(args);
  if ("error" in built) return textTable(analyticsErrorText(new Error(built.error)));
  if ("notAvailable" in built) return textTable(built.notAvailable);
  if ("help" in built) return layHelpResponse(built);
  if ("dates" in built) return layDatesResponse(built, answers[0]);
  if ("curves" in built) return layCurvesResponse(built, answers[0]);
  if ("surfaces" in built) return laySurfacesResponse(built, answers[0]);
  const answer = mergeAnswers(answers);
  if (answer.error) return textTable(analyticsErrorText(answer.error));
  // An answer the relay returned nothing for (or without headers): the AdFin path's text for a missing answer
  // (responses.ts executorResult), not a TypeError from the processor.
  if (!answers.length || answers.some((a) => !(a as AnalyticsAnswer | null | undefined)?.headers)) {
    return textTable(errorMessage(requestorError({ message: "No response for function" })));
  }
  const processor = new FinancialContractsResProcessor(built.subType === "option");
  let response: ClientResponse;
  try {
    response = processor.response(answer, built.options, built.output);
  } catch (e) {
    return textTable(analyticsErrorText(e));
  }
  if (processor.errorAlone) return textTable(`${response.callerValue}`);
  return responseTable(response);
}

/** The grid of a Help request: LSEG lays out its own About table, no answer involved. */
// LSEG: QPSService.process (36844): execute gives null for help, then the response processor's response
function layHelpResponse(built: HelpRequest): Table {
  let response: ClientResponse;
  try {
    response = new AboutResProcessor().response(built.options, built.output);
  } catch (e) {
    return textTable(analyticsErrorText(e));
  }
  return responseTable(response);
}

/** The grid of a DatesAndCalendars request given the service's answer. */
// LSEG: QPSService.process (36844): the requestor's checks, then the response processor's response
function layDatesResponse(built: DatesRequest, raw: unknown): Table {
  if (raw == null) return textTable(errorMessage(requestorError({ message: "No response for function" })));
  const answer = datesAnswer(raw, built.dates);
  const error = datesAnswerError(answer);
  if (error) return textTable(analyticsErrorText(error));
  let response: ClientResponse;
  try {
    response = new DatesResProcessor(built).response(answer);
  } catch (e) {
    return textTable(analyticsErrorText(e));
  }
  return responseTable(response);
}

/** The grid of a VolatilitySurface request given the service's answer. */
// LSEG: QPSService.process (36844): the response processor's response(answer, payload, processorOutput)
function laySurfacesResponse(built: SurfacesRequest, raw: unknown): Table {
  if (raw == null) return textTable(errorMessage(requestorError({ message: "No response for function" })));
  let response: ClientResponse;
  try {
    response = new SurfacesResProcessor(built).response(raw as Record<string, unknown>);
  } catch (e) {
    return textTable(analyticsErrorText(e));
  }
  return responseTable(response);
}

/** The grid of a Curves request given the service's answer. */
// LSEG: QPSService.process (36844): the response processor's response
function layCurvesResponse(built: CurvesRequest, raw: unknown): Table {
  if (raw == null) return textTable(errorMessage(requestorError({ message: "No response for function" })));
  let response: ClientResponse;
  try {
    response = new CurvesResProcessor(built).response(raw as Record<string, unknown>);
  } catch (e) {
    return textTable(analyticsErrorText(e));
  }
  return responseTable(response);
}

const responseTable = (response: ClientResponse): Table =>
  toTable(trimTrailingBlanks(toGrid(response).map((row) => row.map(markAnalyticsCell))), isEmptyCallerValue(response.callerValue));

// ---------------------------------------------------------------------------------------------
// LSEG's ErrorHandler

const UPSTREAM = /^Upstream error:\s*/i;

/**
 * The cell text of a failed analytics request, as LSEG's ErrorHandler (13859) builds it for
 * Type FinancialContracts. `e` is any of:
 *  - the error the interface call threw (an ApiError, `{status, detail}`): a hub 502
 *    `Upstream error: Error code 400 | <IPA's message>` (or an LSEG 422) is IPA's refusal, its
 *    message taken as LSEG's; any other failure (sign-in, network, a 5xx) is the add-in's
 *    usual text (errorToCellText);
 *  - that error's text: one starting `Upstream error: ` is read as the hub's 502 above; any
 *    other text is IPA's message itself;
 *  - an Error (a failed layout: its message), or an LSEG-shaped error `{message, id, status, reason}`.
 * IPA's `Invalid input: Unbindable json. …` heads are dropped, and from ` path ` on the text is
 * cut (an_err_option_no_underlying: "… from the expected path instrumentDefinition…").
 */
export function analyticsErrorText(e: unknown): string {
  if (typeof e === "string" && UPSTREAM.test(e)) return analyticsErrorText({ status: 502, detail: { kind: "hub", message: e } });
  const refusal = searchRefusal(e);
  if (refusal !== null) return errorHandlerMessage({ message: refusal });
  if (typeof (e as { status?: unknown } | null)?.status === "number") return errorToCellText(e, "TR");
  if (typeof e === "string") return errorHandlerMessage({ message: e });
  return errorHandlerMessage(e);
}

/** `key: value` / `key=value` in an error's text, or "" (an error that is not text has none). */
// LSEG: ErrorHandler_13859.getRdpErrorValue (its fallback pattern is not a valid RegExp: it can only fail, giving "")
function getRdpErrorValue(e: unknown, key: string): string {
  if (typeof e !== "string") return "";
  const m = e.match(new RegExp(`${key}[=:]\\s*(["']?.+["']?)`, "mi"));
  if (!m) return "";
  let v = m[1].replace(/'/g, "");
  if (v.includes(key)) v = getRdpErrorValue(v, key);
  return v.endsWith(",") ? v.substring(0, v.length - 1) : v;
}

const UNBINDABLE = /(Invalid input: Unbindable json\. Json error: |Invalid input: Invalid Json\. |Invalid input: Unbindable json\. )/i;

// LSEG: ErrorHandler_13859.getErrorResponse (its message), with getPrimaryMsg for Type FinancialContracts
function errorHandlerMessage(e: unknown): string {
  const err = e as Record<string, unknown> | null | undefined;
  let message = `${err?.message ?? getRdpErrorValue(e, "message")}`;
  if (/(Value cannot be null.\\n)/.test(message)) message = message.replace("\\n", "");
  const status = `${err?.status ?? getRdpErrorValue(e, "status")}`;
  const reason = err?.reason ?? getRdpErrorValue(e, "reason");
  const code = getRdpErrorValue(e, "code");
  if (message === "") {
    message = status;
    if (message === "" && typeof e === "string") message = e;
  }
  const cleaned = message.split("QPS_Lib Business Calendar:").join("").trim().replace(". .", ".");
  let text = code === "401" ? (status !== "" ? status : cleaned) : message !== "" ? message : cleaned; // getPrimaryMsg, type FC
  if (reason) {
    if (text === "Validation error,") text = text.substring(0, text.length - 1);
    text += ": " + `${reason}`.trim();
  }
  return `${text} `.replace(UNBINDABLE, "").replace(/\spath\s.+\s/i, "").trim();
}
